import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, Users } from "lucide-react";
import { trackerListTeam, trackerSaveTeamMember, trackerDeleteTeamMember } from "../../lib/tracker";

const TeamMemberForm = ({ initial, onSave, onCancel, busy }) => {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [role, setRole] = useState(initial?.role || "");
  const [active, setActive] = useState(initial?.active !== false);

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave({ name, email: email || null, role: role || null, active }); }}
      className="p-4 rounded-sm grid gap-3 sm:grid-cols-[1fr_1fr_180px_auto] items-end"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="team-member-form"
    >
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Name</span>
        <input
          type="text" required value={name} onChange={(e) => setName(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          data-testid="team-member-name"
        />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Email (optional)</span>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          data-testid="team-member-email"
        />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Role</span>
        <select
          value={role} onChange={(e) => setRole(e.target.value)}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          data-testid="team-member-role"
        >
          <option value="">(none)</option>
          {["Sales", "Print Manager", "Designer", "Production", "Other"].map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy}
          className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]"
          data-testid="team-member-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : initial?.id ? "Update" : "Add"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-2 py-2 rounded-sm hover:bg-white"><X size={14} /></button>
        )}
      </div>
      <label className="sm:col-span-4 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span>Active (uncheck to hide from dropdowns without deleting)</span>
      </label>
    </form>
  );
};

const TeamMembersTab = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      setMembers(await trackerListTeam());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      await trackerSaveTeamMember(editing?.id, payload);
      toast.success(editing?.id ? "Updated" : "Member added");
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Existing notes & assignments stay intact.`)) return;
    try {
      await trackerDeleteTeamMember(m.id);
      toast.success("Removed");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="p-10 text-[color:var(--ups-subtext)] flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Loading…</div>;

  return (
    <div className="space-y-6" data-testid="team-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Users size={20} /> Team Members</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            People who own jobs, write notes, and appear as Sales POC. The Admin Panel is shared (one password) — these names just power the dropdowns.
          </p>
        </div>
        {!editing && (
          <button onClick={() => setEditing({})} className="btn-brutal px-3 py-2 rounded-sm flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em]" data-testid="add-team-member-btn">
            <Plus size={14} /> Add Member
          </button>
        )}
      </div>
      {editing && <TeamMemberForm initial={editing} busy={busy} onSave={handleSave} onCancel={() => setEditing(null)} />}
      <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
        {members.length === 0 && <div className="p-6 text-sm text-[color:var(--ups-subtext)]">No team members yet — add the first one to start assigning jobs.</div>}
        {members.map((m, idx) => (
          <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3" style={{ borderTop: idx === 0 ? "none" : "1px solid var(--ups-border)" }}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm">{m.name}</span>
                {!m.active && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]" style={{ background: "#FEE2E2", color: "#991B1B" }}>Disabled</span>}
                {m.role && <span className="px-1.5 py-0.5 text-[10px] font-mono" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>{m.role}</span>}
              </div>
              {m.email && <div className="text-[12px] font-mono text-[color:var(--ups-subtext)] mt-0.5">{m.email}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(m)} className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]" aria-label="Edit"><Pencil size={13} /></button>
              <button onClick={() => handleDelete(m)} className="p-1.5 rounded-sm hover:bg-[#FEE2E2]" aria-label="Remove"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamMembersTab;
